import React, { useState, useEffect, useCallback } from "react"
import { Space, Checkbox, Button, Typography, Tag, Divider, Tooltip, Row, Col, Badge, message, Descriptions, Statistic, Form, Input, Modal, Popconfirm, Radio } from "antd"
import PropTypes from "prop-types"
import {
  DatabaseOutlined,
  SwapOutlined,
  EnvironmentOutlined,
  BookOutlined,
  AimOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  MessageOutlined,
  TeamOutlined,
  EyeOutlined,
  GlobalOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  FileTextOutlined,
  BankOutlined,
  TrophyOutlined,
  IdcardOutlined,
  FireOutlined,
  EditOutlined,
  UserOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  CommentOutlined,
  DownOutlined,
  UpOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons"
import moment from "moment"
import cssStyles from './CB.module.css'
import { useCallAPI } from "../../../../../hooks/useCallAPI"
import { CB_NOTES_ADD, CB_NOTES_UPDATE, CB_NOTES_DELETE } from "../../../../../ApiUrl"
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useNotes } from '../context/notesContext';
import axios from "axios"
import { requestHeader } from "../../../../../others/header"

const { Text, Title, Paragraph } = Typography
const { TextArea } = Input;

// Define theme colors
const themeColors = {
  primary: '#52c41a', 
  secondary: '#95de64',
  tertiary: '#389e0d', 
  background: '#f6ffed', 
  border: '#b7eb8f' 
}

const defaultStyles = {
  positionTitle: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "1.2",
    color: "#333",
  },
  companyName: {
    fontSize: "14px",
    lineHeight: "1.2",
    color: "#666",
  },
  tag: {
    fontSize: "12px",
    lineHeight: "1.2",
  },
  candidateCard: {
    background: '#fff',
    padding: "16px",
    borderRadius: "6px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${themeColors.border}`,
    transition: "all 0.3s ease",
  },
  selectedCard: {
    border: `1px solid ${themeColors.primary}`,
    boxShadow: `0 2px 8px rgba(82, 196, 26, 0.2)`,
  },
  viewedCard: {
    borderLeft: `4px solid ${themeColors.primary}`,
    boxShadow: `0 2px 8px rgba(82, 196, 26, 0.15)`,
  },
  cardContent: {
    width: "100%",
  },
  matchPercentage: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "bold",
    color: "#fff",
    textAlign: "center",
    minWidth: "60px",
  },
  nameContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  }
}

const actionBarStyles = {
  position: "absolute",
  bottom: "-20px",
  left: "16px",
  display: "flex",
  alignItems: "center",
  zIndex: 1,
  transition: "opacity 0.3s ease, transform 0.3s ease",
}

const cardWrapperStyles = {
  position: "relative",
  marginBottom: "16px",
}

const CandidateCard = React.memo(
  ({
    candidate,
    isSelected = false,
    onCardClick,
    onCompare,
    compareList = [],
    onExternalView,
    useStyles = defaultStyles,
  }) => {
    const [showActions, setShowActions] = useState(false)
    const [isLocallyViewed, setIsLocallyViewed] = useState(false)
    const [notesData, setNotesData] = useState([])
    const [hasNotes, setHasNotes] = useState(false)
    const [notesExpanded, setNotesExpanded] = useState(false)
    const [notesLoading, setNotesLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingNoteId, setEditingNoteId] = useState(null)
    const [isAddingNote, setIsAddingNote] = useState(false)
    const [noteContent, setNoteContent] = useState('')
    const [noteType, setNoteType] = useState('notes')
    const [form] = Form.useForm();

    const { syncNotes, addNote, updateNote, deleteNote, getCandidateNotes, getLastUpdated, getCandidateDbId, syncCandidateDbId } = useNotes();


    const candidateId = candidate?.sid || candidate?.id;


    const addNoteApi = useCallAPI(CB_NOTES_ADD);
    const updateNoteApi = useCallAPI(CB_NOTES_UPDATE);
    const deleteNotes = async (noteId) => {
      try {
        const candidateDbId = getCandidateDbId(candidate?.sid || candidate?.id) || candidate?.candidateId;


        const res = await axios.delete(`${CB_NOTES_DELETE}/${candidateId}/${noteId}?candidateId=${candidateDbId || null}`,

          requestHeader())


        return res.data;



      }
      catch (err) {
        console.log('error in deleting notes', err)

      }
    }


    useEffect(() => {

      if (candidate.isViewed) {
        setIsLocallyViewed(true)
      }
    }, [candidate.isViewed])

    useEffect(() => {
      if (candidate) {

        if (candidate.notesInfo) {

          const notes = candidate?.notesInfo?.notes || [];

          if (notes && notes.length > 0) {

            const validatedNotes = notes.map(note => ({
              ...note,
              type: note.type || 'notes'
            }));


            setNotesData(validatedNotes);


            setNotesExpanded(true);
          } else {
            setNotesData([]);
          }

          const hasNotesValue = candidate.notesInfo.hasNotes === true || notes.length > 0 ||
            (typeof candidate.notesInfo.notesCount === 'number' && candidate.notesInfo.notesCount > 0);

          setHasNotes(hasNotesValue);

          if (hasNotesValue && !notesExpanded) {

            setNotesExpanded(true);
          }
        } else {
          setNotesData([]);
          setHasNotes(false);
        }
      }
    }, [candidate]);

    useEffect(() => {
      if (!candidateId) return;

      if (candidate?.notesInfo) {
        const contextNotes = getCandidateNotes(candidateId);
        const candidateNotes = candidate.notesInfo.notes || [];

        if (contextNotes.length === 0 && candidateNotes.length > 0) {
          syncNotes(candidateId, candidateNotes);


          setNotesData(candidateNotes);
          setHasNotes(candidateNotes.length > 0);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateId]);

    useEffect(() => {
      if (!candidateId) return;

      const lastUpdateTime = getLastUpdated(candidateId);
      const contextNotes = getCandidateNotes(candidateId);


      if (
        contextNotes.length > 0 &&
        (notesData.length !== contextNotes.length || lastUpdateTime > 0)
      ) {
        ;
        setNotesData(contextNotes);
        setHasNotes(contextNotes.length > 0);
      }
    }, [candidateId, getCandidateNotes, getLastUpdated, notesData.length]);


    const toggleNotes = (e) => {
      if (e) e.stopPropagation();
      setNotesExpanded(!notesExpanded);
    }
    useEffect(() => {
      if (candidate?.candidateId) {
        syncCandidateDbId(candidateId, candidate.candidateId);
      }
    }, [candidate?.candidateId, candidateId, syncCandidateDbId]);
    const handleAddNote = async (noteData) => {
      if (!candidateId) {
        message.error('Cannot add note: Missing candidate ID');
        return;
      }

      setIsSubmitting(true);
      try {
        const sid = candidateId;

        const payload = {
          notes: noteData.notes,
          type: noteData.type || 'notes'
        };

        if (candidate.candidateId) {
          payload.candidateId = candidate.candidateId;
        }
        const contextNotes = getCandidateNotes(candidateId);
        const existingNotes = contextNotes.length > 0 ? [...contextNotes] : [...notesData];
        const response = await addNoteApi.post(payload, sid);

        let newNote;
        let updatedNotes = [];

        if (response?.data?.data) {
          if (Array.isArray(response.data.data)) {
            if (response.data.data.length >= existingNotes.length) {
              updatedNotes = response.data.data;
            } else {

              newNote = response.data.data[0];
              updatedNotes = [...existingNotes, newNote];
            }
          }
          else if (response.data.data.notes && Array.isArray(response.data.data.notes)) {

            if (response.data.data.notes.length >= existingNotes.length) {
              updatedNotes = response.data.data.notes;
            } else {
              newNote = response.data.data.notes[0];
              updatedNotes = [...existingNotes, newNote];
            }
          }
          else {
            newNote = response.data.data;


            updatedNotes = [...existingNotes, newNote];
          }



          setNotesData(updatedNotes);
          setHasNotes(true);

          setTimeout(() => {
            syncNotes(candidateId, updatedNotes);
          }, 100);
        }

        message.success('Note added successfully');


        setIsAddingNote(false);
        setEditingNoteId(null);
        form.resetFields();

        return response;
      } catch (error) {
        console.error('[CandidateCard] Error adding note:', error);
        message.error('Failed to add note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }

    const handleUpdateNote = async (noteId, noteData) => {
      if (!noteId) {
        message.error('Cannot update note: Missing note ID');
        return;
      }
      const dbId = getCandidateDbId(candidateId) || candidate?.candidateId;


      setIsSubmitting(true);
      try {
        const sid = candidateId;
        const payload = {
          notes: noteData.notes,
          type: noteData.type,
        }; if (dbId) {
          payload.candidateId = dbId;
        }

        const response = await updateNoteApi.patch(payload, `${sid}/${noteId}`);


        let updatedNotes;

        if (response?.data?.data?.notes && Array.isArray(response.data.data.notes)) {

          updatedNotes = response.data.data.notes;
        } else {

          updatedNotes = notesData.map(note => {
            if (note._id === noteId) {
              return {
                ...note,
                ...payload,
                updated_at: new Date().toISOString()
              };
            }
            return note;
          });
        }


        setNotesData(updatedNotes);


        setTimeout(() => {
          syncNotes(candidateId, updatedNotes);
        }, 100);

        message.success('Note updated successfully');


        setIsAddingNote(false);
        setEditingNoteId(null);
        form.resetFields();

        return response;
      } catch (error) {
        console.error('[CandidateCard] Error updating note:', error);
        message.error('Failed to update note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }

    const handleDeleteNote = async (noteId) => {
      if (!candidateId) {
        message.error('Cannot delete note: Missing candidate ID');
        return;
      }

      setIsSubmitting(true);
      try {


        const response = await deleteNotes(noteId);

        let updatedNotes;

        if (response?.data?.data?.notes && Array.isArray(response.data.data.notes)) {
          updatedNotes = response.data.data.notes;
        } else if (response?.data?.data?.multipleNotes && Array.isArray(response.data.data.multipleNotes)) {
          updatedNotes = response.data.data.multipleNotes;
        } else {
          updatedNotes = notesData.filter(note => note._id !== noteId);
        }

        deleteNote(candidateId, noteId);

        setNotesData(updatedNotes);
        setHasNotes(updatedNotes.length > 0);

        if (candidate.notesInfo) {
          candidate.notesInfo.notes = updatedNotes;
          candidate.notesInfo.notesCount = updatedNotes.length;
          candidate.notesInfo.hasNotes = updatedNotes.length > 0;
        }

        setTimeout(() => {
          syncNotes(candidateId, updatedNotes);
        }, 150);
        const nostesss = getCandidateNotes(candidateId)

        message.success('Note deleted successfully');

        if (updatedNotes.length === 0) {
          setNotesExpanded(false);
          syncNotes(candidateId, []);
        }

        return response;
      } catch (error) {
        console.error('[Monster Card] Error deleting note:', error);
        message.error('Failed to delete note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    };


    const confirmDeleteNote = (noteId) => {
      Modal.confirm({
        title: 'Delete Note',
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        content: 'Are you sure you want to delete this note? This action cannot be undone.',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          return handleDeleteNote(noteId);
        },
        okButtonProps: {
          loading: isSubmitting
        }
      });
    };


    const startAddNote = (e) => {
      if (e) e.stopPropagation();
      setIsAddingNote(true);
      setEditingNoteId(null);
      setNoteContent('');
      setNoteType('notes');
      form.resetFields();
    }


    const startEditNote = (e, note) => {
      if (e) e.stopPropagation();
      setIsAddingNote(true);
      setEditingNoteId(note._id);


      form.resetFields();
      form.setFieldsValue({
        type: note.type || 'notes',
        notes: note.notes
      });
    }


    const cancelNote = (e) => {
      if (e) e.stopPropagation();
      setIsAddingNote(false);
      setEditingNoteId(null);
      form.resetFields();
    }

    const submitNote = (e) => {
      if (e) e.stopPropagation();

      form.validateFields().then(values => {
        if (editingNoteId) {
          handleUpdateNote(editingNoteId, values);
        } else {
          handleAddNote(values);
        }
      }).catch(error => {
        console.error("Validation failed:", error);
      });
    }
    useEffect(() => {
      if (candidateId) {
        const contextNotes = getCandidateNotes(candidateId);


        if (contextNotes?.length >= 0 && contextNotes.length !== notesData.length) {
          setNotesData(contextNotes);
          setHasNotes(contextNotes.length > 0);

          if (candidate.notesInfo) {
            candidate.notesInfo.notes = contextNotes;
            candidate.notesInfo.notesCount = contextNotes.length;
            candidate.notesInfo.hasNotes = contextNotes.length > 0;
          }
        }
      }
    }, [candidateId, getCandidateNotes]);

    if (!candidate || !candidate.id) {
      console.warn("CandidateCard received invalid candidate data")
      return null
    }


    const renderLocation = () => {

      if (candidate._original && candidate._original.HomeLocation && candidate._original.HomeLocation[0]) {
        const locationString = candidate._original.HomeLocation[0];
        const locationParts = locationString.split('-');
        if (locationParts.length >= 3) {
          const [country, state, city] = locationParts;
          return `${city}, ${state}, ${country}`;
        }
        return locationString;
      }

      const locationDetails = candidate.locationDetails || {}
      const parts = [
        locationDetails.city,
        locationDetails.stateName,
        locationDetails.countryName,
        locationDetails.postalCode,
      ].filter(Boolean)

      return parts.length > 0
        ? parts.join(", ")
        : locationDetails.fullName || candidate.location || "Location not specified"
    }


    const formatDate = (dateStr) => {
      if (!dateStr) return "";


      if (typeof dateStr === 'object') {
        const year = dateStr.Year || "";
        const month = dateStr.Month || "";
        return year && month ? `${month}/${year}` : "";
      }


      try {
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const [year, month, day] = parts;
            return `${month}/${day}/${year}`;
          }
        }

        return moment(dateStr).format("MM/DD/YYYY");
      } catch (e) {
        return dateStr;
      }
    }


    const formatExperience = (months) => {
      if (!months) return "Not specified";

      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;

      if (years > 0 && remainingMonths > 0) {
        return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
      } else if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''}`;
      } else {
        return `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
      }
    };


    const formatTimeAgo = (dateStr) => {
      if (!dateStr) return "";

      try {
        return moment(dateStr).fromNow();
      } catch (e) {
        return dateStr;
      }
    }


    const getEducation = () => {
      if (candidate._original && candidate._original.HighestDegree && candidate._original.HighestDegree[0]) {
        return candidate._original.HighestDegree[0];
      }
      return candidate.education || "Not specified";
    }


    const getExperience = () => {
      if (candidate._original && candidate._original.MonthsOfExperience && candidate._original.MonthsOfExperience[0]) {
        return formatExperience(parseInt(candidate._original.MonthsOfExperience[0]));
      }
      return candidate.experience ? formatExperience(candidate.experience.raw) : "Not specified";
    }


    const getLastUpdate = () => {
      if (candidate._original && candidate._original.LastUpdate && candidate._original.LastUpdate[0]) {
        return formatDate(candidate._original.LastUpdate[0]);
      }
      return candidate.updatedDate ? formatDate(candidate.updatedDate) : "Not specified";
    }


    const getLastActivity = () => {
      if (candidate._original && candidate._original.LastActivity && candidate._original.LastActivity[0]) {
        return candidate._original.LastActivity[0];
      }
      return "Not specified";
    }


    const renderProfileBadges = () => {
      return (
        <Space wrap>
          {(candidate.isViewed || isLocallyViewed) && (
            <Tag icon={<EyeOutlined />} color="success">Viewed</Tag>
          )}
          {candidate.isInDb && (
            <Tag icon={<DatabaseOutlined />} color="success">In Database</Tag>
          )}
          {candidate.isConfidential && (
            <Tag icon={<LockOutlined />} color="error">Confidential</Tag>
          )}
        </Space>
      );
    }


    const renderCandidateStatuses = () => {
      return (
        <Row gutter={[8, 8]}>
          {candidate._original?.viewedHist?.length > 0 && (
            <Col>
              <Tooltip title={`Viewed ${candidate._original.viewedHist.length} times by ${candidate._original.viewedHist.map(h => h.name).join(', ')}`}>
                <Tag icon={<HistoryOutlined />}>{candidate._original.viewedHist.reduce((sum, h) => sum + h.count, 0)} Views</Tag>
              </Tooltip>
            </Col>
          )}
          {candidate.metadata?.isVeteran && (
            <Col>
              <Tag icon={<SafetyCertificateOutlined />}>Veteran</Tag>
            </Col>
          )}
          {candidate.metadata?.isDiversity && (
            <Col>
              <Tag icon={<TeamOutlined />}>Diversity</Tag>
            </Col>
          )}
          {candidate.metadata?.smsOptIn && (
            <Col>
              <Tag icon={<MessageOutlined />}>SMS Opt-in</Tag>
            </Col>
          )}
        </Row>
      );
    }


    const renderNotes = () => {
      return (
        <div
          className={`${cssStyles.notesSection}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '16px',
            background: themeColors.background,
            borderTop: `1px solid ${themeColors.border}`,
            borderRadius: '0 0 6px 6px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong><CommentOutlined /> Notes ({notesData.length})</Text>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={startAddNote}
              disabled={isAddingNote || editingNoteId}
            >
              Add Note
            </Button>
          </div>

          {isAddingNote ? (
            <div style={{ marginBottom: '16px', padding: '12px', border: `1px solid ${themeColors.border}`, borderRadius: '4px', background: '#fff' }}>
              <Form form={form} layout="vertical">
                <Form.Item
                  label="Type"
                  name="type"
                  initialValue="notes"
                  rules={[{ required: true, message: 'Please select a note type' }]}
                >
                  <Radio.Group buttonStyle="solid" defaultValue="notes" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', width: '100%' }}>
                    <Radio.Button value="call" style={{ textAlign: 'center', margin: 0 }}>Call</Radio.Button>
                    <Radio.Button value="meeting" style={{ textAlign: 'center', margin: 0 }}>Meeting</Radio.Button>
                    <Radio.Button value="notes" style={{ textAlign: 'center', margin: 0 }}>Note</Radio.Button>
                    <Radio.Button value="others" style={{ textAlign: 'center', margin: 0 }}>Other</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="notes"
                  label="Note"
                  rules={[{ required: true, message: 'Please enter note content' }]}
                >
                  <TextArea rows={4} />
                </Form.Item>

                <Row justify="end" gutter={8}>
                  <Col>
                    <Button
                      onClick={cancelNote}
                      icon={<CloseOutlined />}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      onClick={submitNote}
                      loading={notesLoading}
                      icon={<SaveOutlined />}
                    >
                      {editingNoteId ? 'Update' : 'Save'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          ) : notesData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Text type="secondary">No notes yet. Click "Add Note" to create one.</Text>
            </div>
          ) : (
            <div>
              {notesData.map((note, index) => (
                <div
                  key={note._id || index}
                  style={{
                    marginBottom: '10px',
                    padding: '10px',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '4px',
                    background: '#fff'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text strong style={{ textTransform: 'capitalize' }}>{note?.type || 'notes'}</Text>
                    <Space>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => startEditNote(e, note)}
                        disabled={isAddingNote || (editingNoteId && editingNoteId !== note?._id)}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteNote(note?._id);
                        }}
                        disabled={notesLoading}
                      />
                    </Space>
                  </div>

                  <div style={{ margin: '10px 0' }}>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                      {note.notes}
                    </Text>
                  </div>

                  <div>
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(note.created_at).format('MMM DD, YYYY [at] hh:mm A')}
                        {note.updated_at && note.created_at !== note.updated_at &&
                          ` • Updated ${moment(note.updated_at).format('MMM DD, YYYY [at] hh:mm A')}`
                        }
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        By: {note.created_by?.display_name || note.created_by?.name || 'Unknown'}
                      </Text>
                    </Space>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    if (!candidate || !candidate.id) {
      console.warn("CandidateCard received invalid candidate data")
      return null
    }

    const isCompared = Array.isArray(compareList) && compareList.includes(candidate.id)

    const handleCardClick = () => {
      setIsLocallyViewed(true)



      onCardClick?.(candidate)
    }

    const handleCompareClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      onCompare?.(e, candidate.id)
    }

    return (
      <div
        style={cardWrapperStyles}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        className={`${cssStyles.candidateCardWrapper} ${cssStyles.cbContainer} ${isSelected ? "selected" : ""}`}
      >
        <div
          style={{
            ...actionBarStyles,
            opacity: showActions ? 1 : 0,
            transform: `translateY(${showActions ? "0" : "20px"})`,
            pointerEvents: showActions ? "auto" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
          className={cssStyles.actionBar}
        >
          <Tooltip title={isCompared ? "Remove from Compare" : "Add to Compare"}>
            <Button
              icon={<SwapOutlined />}
              size="middle"
              type={isCompared ? "primary" : "default"}
              onClick={handleCompareClick}
            />
          </Tooltip>
          <Tooltip title="Open in New Tab">
            <Button
              icon={<LinkOutlined />}
              size="middle"
              onClick={(e) => {
                e.stopPropagation()
                onExternalView?.(candidate)
              }}
            />
          </Tooltip>
          <Tooltip title={notesExpanded ? "Hide Notes" : "View Notes"}>
            <Button
              icon={<FileTextOutlined />}
              size="middle"
              type={hasNotes || notesExpanded ? "primary" : "default"}
              onClick={toggleNotes}
              badge={{ count: notesData?.length || 0, showZero: false }}
            />
          </Tooltip>
        </div>
        <div
          onClick={handleCardClick}
          className={`${cssStyles.candidateCardHover} ${isLocallyViewed ? cssStyles.candidateCardViewed : ""
            }`}
          style={{
            ...useStyles.candidateCard,
            ...(isSelected ? useStyles.selectedCard : {}),
            ...(candidate.isViewed || isLocallyViewed ? useStyles.viewedCard : {}),
            cursor: "pointer",
            position: "relative",
            borderBottomLeftRadius: notesExpanded ? 0 : '6px',
            borderBottomRightRadius: notesExpanded ? 0 : '6px',
          }}
        >
          {hasNotes && (
            <div
              className={cssStyles.notesIndicator}
              title="Click to view notes"
              onClick={(e) => {
                e.stopPropagation();
                toggleNotes(e);
              }}
              style={{
                top: '8px',
                right: '8px'
              }}
            >
              <FileTextOutlined style={{ fontSize: '10px' }} />
              <span>{notesData.length}</span>
            </div>
          )}

          <div style={useStyles.cardContent}>
            {/* Header Section */}
            <div style={{ marginBottom: "12px" }}>
              <Row justify="space-between" align="top">
                <Col flex="1">
                  <Space direction="vertical" size={1} style={{ width: "100%" }}>
                    <div style={useStyles.nameContainer}>
                      <Text style={{ ...useStyles.positionTitle, fontSize: "16px" }}>
                        {candidate._original?.ContactName?.[0] || candidate.name || "Unnamed Candidate"}
                      </Text>
                    </div>
                    {/* Resume Title / Job Title */}
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {candidate._original?.ResumeTitle?.[0] || candidate._original?.JobTitle?.[0] || candidate.title || "No Title"}
                      </Text>
                    </div>
                    {/* Location */}
                    <div style={{ marginTop: '4px' }}>
                      <Text type="secondary">
                        <EnvironmentOutlined style={{ marginRight: "4px" }} />
                        {renderLocation()}
                      </Text>
                    </div>
                  </Space>
                </Col>
              </Row>
            </div>

            {/* Status indicators */}
            <Row justify="space-between" align="middle" style={{ marginBottom: "12px" }}>
              <Col>
                <Space>
                  <Checkbox
                    checked={isCompared}
                    onChange={handleCompareClick}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {candidate._original?.ResumeID?.[0] && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      ID: {candidate._original.ResumeID[0]}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col>
                {renderProfileBadges()}
              </Col>
            </Row>

            {/* Key Stats Section */}
            <div style={{ marginBottom: "16px" }}>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <Statistic
                    title="Experience"
                    value={getExperience()}
                    valueStyle={{ fontSize: '14px' }}
                    prefix={<TrophyOutlined style={{ color: themeColors.primary }} />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Education"
                    value={getEducation()}
                    valueStyle={{ fontSize: '14px' }}
                    prefix={<BookOutlined style={{ color: themeColors.tertiary }} />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Updated"
                    value={getLastUpdate()}
                    valueStyle={{ fontSize: '14px' }}
                    prefix={<CalendarOutlined style={{ color: themeColors.primary }} />}
                  />
                </Col>
              </Row>
            </div>

            {/* Current Position Section */}
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                <BankOutlined style={{ marginRight: "8px", color: themeColors.primary }} />
                Current Position
              </Text>
              <div
                style={{
                  marginBottom: "12px",
                  background: "#fff",
                  padding: "12px",
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: "4px",
                }}
              >
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  {/* Job Title */}
                  <div>
                    <Text strong>
                      {candidate._original?.RecentJobTitle?.[0] ||
                        candidate.currentPosition?.title ||
                        candidate.position?.title ||
                        "Title not specified"}
                    </Text>
                  </div>

                  {/* Company */}
                  <div>
                    <Text type="secondary">
                      at {candidate._original?.RecentEmployer?.[0] ||
                        candidate.currentPosition?.company ||
                        candidate.position?.company ||
                        "Company not specified"}
                    </Text>
                  </div>
                  {/* Salary info if available */}
                  {candidate._original?.RecentPay?.[0] && (
                    <div>
                      <Text type="secondary">
                        Salary: {candidate._original.RecentPay[0]}
                      </Text>
                    </div>
                  )}
                </Space>
              </div>
            </div>

            {/* Candidate statuses and tags */}
            <div style={{ marginBottom: "16px" }}>
              {renderCandidateStatuses()}
            </div>

            {/* Footer Section */}
            <Divider style={{ margin: "12px 0", borderColor: themeColors.border }} />
            <Row justify="space-between" align="middle">
              <Col>
                <Space split={<Divider type="vertical" style={{ borderColor: themeColors.border }} />} style={{ width: "100%" }}>
                  {/* Last Activity */}
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    <FieldTimeOutlined style={{ marginRight: "4px", color: themeColors.primary }} />
                    Last Activity: {formatTimeAgo(getLastActivity())}
                  </Text>
                  {/* Source */}
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Source: {candidate.source || "CareerBuilder"}
                  </Text>
                </Space>
              </Col>
              <Col>
                {/* Identifiers */}
                <Space>
                  {candidate.candidateId && (
                    <Tooltip title="Candidate ID">
                      <Tag icon={<IdcardOutlined />} color="success">{candidate.candidateId.substring(0, 8)}...</Tag>
                    </Tooltip>
                  )}
                  {candidate._original?.attachments && (
                    <Tooltip title="Has Resume Attachment">
                      <Tag icon={<FileTextOutlined />} color="success">Resume</Tag>
                    </Tooltip>
                  )}
                </Space>
              </Col>
            </Row>
          </div>
        </div>

        {/* Expandable Notes Section */}
        {notesExpanded && renderNotes()}
      </div>
    )
  },
)

CandidateCard.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    location: PropTypes.string,
    locationDetails: PropTypes.object,
    currentPosition: PropTypes.object,
    previousPosition: PropTypes.object,
    metadata: PropTypes.object,
    targetInfo: PropTypes.object,
    education: PropTypes.string,
    workAuth: PropTypes.object,
    dateModified: PropTypes.string,
    dateCreated: PropTypes.string,
    isConfidential: PropTypes.bool,
    isViewed: PropTypes.bool,
    board: PropTypes.string,
    viewHistory: PropTypes.array,
    resumeTitle: PropTypes.string,
    candidateId: PropTypes.string,
    relevance: PropTypes.number,
    isInDb: PropTypes.bool,
    notes: PropTypes.array,
    notesLocation: PropTypes.string,
    candidateDbId: PropTypes.string,
    pendingNotesId: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool,
  onCardClick: PropTypes.func,
  onCompare: PropTypes.func,
  compareList: PropTypes.arrayOf(PropTypes.string),
  onExternalView: PropTypes.func,
  useStyles: PropTypes.object,
}

CandidateCard.displayName = "CandidateCard"

export default CandidateCard

